import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../lib/cn.ts";
import { IconAlertCircle, IconCheckCircle, IconClose, IconInfo, IconWarning } from "./icons.tsx";

/**
 * Toasts.
 *
 * Existem para dar o "feedback imediato" que o briefing exige — sobretudo no
 * rollback de arraste, onde o usuário precisa entender por que o evento voltou
 * para o lugar anterior.
 *
 * Toasts de erro **não** expiram sozinhos: uma falha que desaparece antes de
 * ser lida é uma falha escondida.
 */

export type ToastTone = "success" | "error" | "info" | "warning";

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** `null` = permanece até ser fechado. Padrão de erro. */
  durationMs?: number | null;
}

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id">) => string;
  dismiss: (id: string) => void;
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (context === null) {
    throw new Error("useToast precisa estar dentro de <ToastProvider>");
  }
  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = crypto.randomUUID();
      const duration =
        toast.durationMs === undefined ? (toast.tone === "error" ? null : 4500) : toast.durationMs;

      setToasts((current) => [...current.slice(-4), { ...toast, id }]);

      if (duration !== null) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }

      return id;
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      push,
      dismiss,
      success: (title, description) => push({ tone: "success", title, description }),
      error: (title, description) => push({ tone: "error", title, description }),
      info: (title, description) => push({ tone: "info", title, description }),
    }),
    [toasts, push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

const TONE_STYLES: Record<ToastTone, { wrapper: string; icon: ReactNode }> = {
  success: {
    wrapper:
      "border-[var(--dream-success-border)] bg-[var(--dream-success-bg)] text-[var(--dream-success-fg)]",
    icon: <IconCheckCircle className="size-4 text-[var(--dream-success-fg)]" />,
  },
  error: {
    wrapper:
      "border-[var(--dream-danger-border)] bg-[var(--dream-danger-bg)] text-[var(--dream-danger-fg)]",
    icon: <IconAlertCircle className="size-4 text-[var(--dream-danger-fg)]" />,
  },
  warning: {
    wrapper:
      "border-[var(--dream-warning-border)] bg-[var(--dream-warning-bg)] text-[var(--dream-warning-fg)]",
    icon: <IconWarning className="size-4 text-[var(--dream-warning-fg)]" />,
  },
  info: {
    wrapper:
      "border-[var(--dream-info-border)] bg-[var(--dream-info-bg)] text-[var(--dream-info-fg)]",
    icon: <IconInfo className="size-4 text-[var(--dream-info-fg)]" />,
  },
};

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}) {
  return (
    <section
      // `polite` para não interromper o que o leitor de tela já está falando.
      aria-live="polite"
      aria-label="Notificações"
      className="pointer-events-none fixed inset-x-3 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[60] flex flex-col gap-2 sm:inset-x-auto sm:right-4 sm:w-[min(24rem,calc(100vw-2rem))]"
    >
      {toasts.map((toast) => {
        const style = TONE_STYLES[toast.tone];
        return (
          <div
            key={toast.id}
            data-testid={`toast-${toast.tone}`}
            className={cn(
              "pointer-events-auto flex items-start gap-2.5 rounded-[var(--radius-lg)] border p-3 shadow-md",
              "dream-toast-enter motion-reduce:animate-none",
              style.wrapper,
            )}
          >
            <span className="mt-0.5 shrink-0">{style.icon}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[0.8125rem] font-semibold">{toast.title}</p>
              {toast.description !== undefined && (
                <p className="mt-0.5 text-[0.8125rem] leading-5 opacity-90">{toast.description}</p>
              )}
            </div>
            <button
              type="button"
              aria-label="Dispensar notificação"
              onClick={() => onDismiss(toast.id)}
              className="-mr-1 -mt-1 flex size-6 shrink-0 items-center justify-center rounded opacity-60 transition-opacity hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-1"
            >
              <IconClose className="size-3.5" />
            </button>
          </div>
        );
      })}
    </section>
  );
}
